"use client";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { getSessionUser, getLicensedUserCount, getPlans, createTenant } from "@/services/api";
import ContactSalesModal from "@/components/ContactSalesModal";
import BrandLogo from "@/components/BrandLogo";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Building2,
  Mail,
  User,
  Users,
  HardDrive,
  Phone,
  ArrowRight,
  ArrowLeft,
  Shield,
  CheckCircle2,
  CreditCard,
  Landmark,
  Clock,
  BarChart3,
  Lock,
  Server,
  Database,
  PartyPopper,
  Check,
  AlertTriangle,
  Loader2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const STEPS = 5;

const planOptions = [
  { id: "core", key: "core", storage: "10 TB", monthlyPrice: "€399", annualPrice: "€359", monthlyNum: 399, annualNum: 359, custom: false },
  { id: "standard", key: "standard", storage: "50 TB", monthlyPrice: "€1,499", annualPrice: "€1,349", monthlyNum: 1499, annualNum: 1349, custom: false, recommended: true },
  { id: "enterprise", key: "enterprise", storage: "100 TB+", monthlyPrice: "", annualPrice: "", monthlyNum: 0, annualNum: 0, custom: true },
];

const storageExtensions = [
  { id: "ext25", volume: "+25 TB", price: "€450", perTb: "€18", monthlyNum: 450 },
  { id: "ext50", volume: "+50 TB", price: "€850", perTb: "€17", monthlyNum: 850 },
  { id: "ext100", volume: "+100 TB", price: "€1,600", perTb: "€16", monthlyNum: 1600 },
];

const ANNUAL_DISCOUNT = 0.15;

function buildAdminConsentUrl(): string | null {
  const clientId = import.meta.env.VITE_MSAL_CLIENT_ID;
  if (!clientId) return null;

  const tenantId = import.meta.env.VITE_MSAL_TENANT_ID ?? "common";
  const redirectUri = `${window.location.origin}/enrollment/admin-consent-callback`;
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
  });

  return `https://login.microsoftonline.com/${encodeURIComponent(tenantId)}/adminconsent?${params.toString()}`;
}
export default function Enrollment() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);

  // Step 1 form
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    getSessionUser().then((user) => {
      if (user) {
        if (user.email) setEmail(user.email);
        if (user.name) setName(user.name);
      }
    });
  }, []);

  // Listen for admin consent result from popup
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data;
      if (data?.type === "resilience365-admin-consent") {
        setConsentLoading(false);
        if (data.success) setStep((s) => Math.min(s + 1, STEPS - 1));
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  // Step 3: licensed user count (loaded when entering step 3)
  const [licensedUserCount, setLicensedUserCount] = useState<number | null>(null);
  const [loadingLicensedUsers, setLoadingLicensedUsers] = useState(false);

  // Load licensed user count when entering step 3 (plan selection)
  useEffect(() => {
    if (step !== 2 || licensedUserCount !== null || loadingLicensedUsers) return;
    setLoadingLicensedUsers(true);
    getLicensedUserCount()
      .then((data) => {
        const count = data.licensedUserCount;
        setLicensedUserCount(count);
        // Auto-select plan: <500 core, 500–2500 standard, >2500 enterprise
        if (count < 500) setSelectedPlan("core");
        else if (count <= 2500) setSelectedPlan("standard");
        else setSelectedPlan("enterprise");
      })
      .catch(() => setLicensedUserCount(0))
      .finally(() => setLoadingLicensedUsers(false));
  }, [step, licensedUserCount, loadingLicensedUsers]);

  // Step 3 plan selection
  const [selectedPlan, setSelectedPlan] = useState("standard");
  const [billingAnnual, setBillingAnnual] = useState(false);
  const [selectedExtensions, setSelectedExtensions] = useState<string[]>([]);

  // Step 4 payment
  const [paymentMethod, setPaymentMethod] = useState<"card" | "sepa">("card");

  const isCustomPlan = selectedPlan === "enterprise";
  const [salesModalOpen, setSalesModalOpen] = useState(false);
  const [consentLoading, setConsentLoading] = useState(false);
  const [integrateLoading, setIntegrateLoading] = useState(false);

  const toggleExtension = (id: string) => {
    setSelectedExtensions((prev) =>
      prev.includes(id) ? prev.filter((e) => e !== id) : [...prev, id]
    );
  };

  const getSelectedPlanStorage = () => {
    const plan = planOptions.find((p) => p.id === selectedPlan);
    if (!plan) return "50 TB";
    const extraStorage = selectedExtensions.reduce((acc, extId) => {
      const ext = storageExtensions.find((e) => e.id === extId);
      return acc + (ext ? parseInt(ext.volume.replace(/[^0-9]/g, "")) : 0);
    }, 0);
    const baseLabel = plan.storage;
    return extraStorage > 0 ? `${baseLabel} + ${extraStorage} TB` : baseLabel;
  };

  const getCostSummary = () => {
    const plan = planOptions.find((p) => p.id === selectedPlan);
    if (!plan || plan.custom) return null;

    const planMonthly = billingAnnual ? plan.annualNum : plan.monthlyNum;

    const extMonthly = selectedExtensions.reduce((acc, extId) => {
      const ext = storageExtensions.find((e) => e.id === extId);
      if (!ext) return acc;
      const price = billingAnnual ? Math.round(ext.monthlyNum * (1 - ANNUAL_DISCOUNT)) : ext.monthlyNum;
      return acc + price;
    }, 0);

    const totalMonthly = planMonthly + extMonthly;
    const totalAnnual = totalMonthly * 12;

    return { planMonthly, extMonthly, totalMonthly, totalAnnual };
  };

  const formatPrice = (n: number) => `€${n.toLocaleString("en-US")}`;
  const getExtPrice = (ext: typeof storageExtensions[0]) => {
    if (billingAnnual) {
      return `€${Math.round(ext.monthlyNum * (1 - ANNUAL_DISCOUNT)).toLocaleString("en-US")}`;
    }
    return ext.price;
  };

  const validateStep1 = () => {
    const e: Record<string, string> = {};
    if (!company.trim()) e.company = t("enrollment.required");
    if (!email.trim()) e.email = t("enrollment.required");
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = t("enrollment.invalidEmail");
    if (!name.trim()) e.name = t("enrollment.required");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => {
    if (step === 0 && !validateStep1()) return;
    setStep((s) => Math.min(s + 1, STEPS - 1));
  };
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  const planKeyToTitle: Record<string, string> = {
    core: "Starter",
    standard: "Standard",
    enterprise: "Entreprise",
  };

  const handleIntegrateClick = async () => {
    setIntegrateLoading(true);
    try {
      const user = await getSessionUser();
      if (!user?.id || !user?.entraTenantId) {
        setIntegrateLoading(false);
        return;
      }
      const plansList = await getPlans();
      const planTitle = planKeyToTitle[selectedPlan] ?? "Standard";
      const plan = plansList.find((p) => p.title === planTitle);
      const planId = plan?.id;
      if (!planId) {
        setIntegrateLoading(false);
        return;
      }
      await createTenant({
        name: company.trim() || "Tenant",
        entraTenantId: user.entraTenantId,
        entraUserId: user.id,
        planId,
        email: email.trim() || undefined,
      });
      setStep(5 as any);
    } catch {
      // Keep loading off; could show error toast
    } finally {
      setIntegrateLoading(false);
    }
  };

  const handleAuthorizeClick = async () => {
    setConsentLoading(true);
    try {
      const url = buildAdminConsentUrl();
      if (!url) {
        setConsentLoading(false);
        return;
      }
      const width = 500;
      const height = 600;
      const left = Math.round((window.screen.width - width) / 2);
      const top = Math.round((window.screen.height - height) / 2);
      const popup = window.open(
        url,
        "resilience365-admin-consent",
        `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
      );
      const poll = setInterval(() => {
        if (!popup?.closed) return;
        clearInterval(poll);
        setConsentLoading(false);
      }, 300);
    } catch {
      setConsentLoading(false);
    }
  };

  // Simulated tenant info; userCount from licensed user count (loaded on step 3)
  const tenantDomain = `${company.toLowerCase().replace(/\s+/g, "")}.onmicrosoft.com`;
  const userCount = licensedUserCount ?? 0;

  const stepIndicator = (
    <div className="flex items-center justify-center gap-2 mb-8">
      {Array.from({ length: STEPS }).map((_, i) => (
        <div
          key={i}
          className={`h-2 rounded-full transition-all duration-300 ${
            i === step ? "w-8 bg-primary" : i < step ? "w-2 bg-accent" : "w-2 bg-border"
          }`}
        />
      ))}
    </div>
  );

  const pageVariants = {
    enter: { opacity: 0, x: 40 },
    center: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -40 },
  };

  return (
    <>
    <ContactSalesModal
      open={salesModalOpen}
      onOpenChange={setSalesModalOpen}
      defaultEmail={email}
      defaultPhone={phone}
      onConfirmed={() => navigate("/")}
    />
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="border-b border-border px-6 py-4 flex items-center justify-between">
        <BrandLogo asLink imgClassName="h-8" />
        <span className="text-sm text-muted-foreground">
          {String(t("enrollment.stepOf", { current: step + 1, total: STEPS } as any))}
        </span>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-2xl">
          {stepIndicator}

          <AnimatePresence mode="wait">
            {/* ───────── STEP 1: Company Info ───────── */}
            {step === 0 && (
              <motion.div key="step1" variants={pageVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3 }}>
                <Card className="p-8 border-border bg-card">
                  <h2 className="font-display text-2xl font-bold text-foreground mb-1">{String(t("enrollment.step1Title"))}</h2>
                  <p className="text-muted-foreground mb-6 text-sm">{String(t("enrollment.step1Subtitle"))}</p>

                  <div className="space-y-4">
                    <div>
                      <Label className="flex items-center gap-1.5 mb-1.5">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        {String(t("enrollment.companyName"))} *
                      </Label>
                      <Input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Acme Corp" />
                      {errors.company && <p className="text-destructive text-xs mt-1">{errors.company}</p>}
                    </div>

                    <div>
                      <Label className="flex items-center gap-1.5 mb-1.5">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        {String(t("enrollment.adminEmail"))} *
                      </Label>
                      <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@acmecorp.com" />
                      {errors.email && <p className="text-destructive text-xs mt-1">{errors.email}</p>}
                    </div>

                    <div>
                      <Label className="flex items-center gap-1.5 mb-1.5">
                        <User className="h-4 w-4 text-muted-foreground" />
                        {String(t("enrollment.name"))} *
                      </Label>
                      <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="John Doe" />
                      {errors.name && <p className="text-destructive text-xs mt-1">{errors.name}</p>}
                    </div>

                    <div>
                      <Label className="flex items-center gap-1.5 mb-1.5">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        {String(t("enrollment.phone"))}
                      </Label>
                      <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+33 1 23 45 67 89" />
                    </div>
                  </div>

                  <div className="mt-8 flex justify-end">
                    <Button onClick={next} className="gap-2">
                      {String(t("enrollment.continue"))} <ArrowRight className="h-4 w-4" />
                    </Button>
                  </div>
                </Card>
              </motion.div>
            )}

            {/* ───────── STEP 2: Microsoft 365 Auth ───────── */}
            {step === 1 && (
              <motion.div key="step2" variants={pageVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3 }}>
                <Card className="p-8 border-border bg-card text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 mb-4">
                    <BrandLogo variant="mark" imgClassName="h-10 w-10" />
                  </div>
                  <h2 className="font-display text-2xl font-bold text-foreground mb-1">{String(t("enrollment.step2Title"))}</h2>
                  <p className="text-muted-foreground text-sm mb-8 max-w-md mx-auto">{String(t("enrollment.step2Subtitle", { company: "Resilience365" } as any))}</p>

                  <Card className="max-w-sm mx-auto p-6 border-border bg-secondary/30">
                    <p className="text-sm text-foreground font-medium mb-3">{String(t("enrollment.readAccessTo"))}</p>
                    <ul className="text-left text-sm text-muted-foreground space-y-2 mb-4">
                      <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-accent" /> Exchange Online</li>
                      <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-accent" /> SharePoint Online</li>
                      <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-accent" /> Entra ID (directory)</li>
                    </ul>
                    <Alert className="mb-6 border-amber-500/50 bg-amber-500/10 text-amber-800 dark:text-amber-200 [&>svg]:text-amber-600 dark:[&>svg]:text-amber-400">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>{String(t("enrollment.tenantAdminRequired"))}</AlertDescription>
                    </Alert>
                    <Button onClick={handleAuthorizeClick} disabled={consentLoading} className="w-full gap-2">
                      {consentLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Lock className="h-4 w-4" />
                      )}{" "}
                      {String(t("enrollment.authorize"))}
                    </Button>
                  </Card>

                  <div className="mt-6 flex justify-start">
                    <Button variant="ghost" onClick={prev} className="gap-2">
                      <ArrowLeft className="h-4 w-4" /> {String(t("enrollment.back"))}
                    </Button>
                  </div>
                </Card>
              </motion.div>
            )}

            {/* ───────── STEP 3: Tenant Detected + Plan Selection ───────── */}
            {step === 2 && (
              <motion.div key="step3" variants={pageVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3 }}>
                <Card className="p-8 border-border bg-card">
                  {/* Success banner */}
                  <div className="flex items-center gap-3 mb-6 p-4 rounded-lg bg-accent/10 border border-accent/20">
                    {loadingLicensedUsers ? (
                      <Loader2 className="h-6 w-6 text-accent flex-shrink-0 animate-spin" />
                    ) : (
                      <CheckCircle2 className="h-6 w-6 text-accent flex-shrink-0" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-foreground">{String(t("enrollment.tenantDetected"))}: {tenantDomain}</p>
                      <p className="text-sm text-muted-foreground">
                        {loadingLicensedUsers ? (
                          <span className="inline-flex items-center gap-1.5">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            {String(t("enrollment.loadingLicensedUsers", "Loading licensed users…"))}
                          </span>
                        ) : (
                          <>
                            {userCount.toLocaleString()} {String(t("enrollment.users"))} {String(t("enrollment.licenses"))}
                          </>
                        )}
                      </p>
                    </div>
                  </div>

                  {/* Automatic provisions */}
                  <div className="space-y-2 mb-8">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">{String(t("enrollment.autoProvisioned"))}</p>
                    {[
                      { icon: Lock, text: t("enrollment.provDek") },
                      { icon: Shield, text: t("enrollment.provKek") },
                      { icon: Server, text: String(t("enrollment.provS3", { company: company.toLowerCase().replace(/\s+/g, "") } as any)) },
                      { icon: Database, text: t("enrollment.provDb") },
                      { icon: HardDrive, text: t("enrollment.provQuota") },
                    ].map(({ icon: Icon, text }, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-foreground">
                        <Icon className="h-4 w-4 text-accent" />
                        <span>{text}</span>
                      </div>
                    ))}
                  </div>

                  {/* Plan Selection - mirroring homepage pricing */}
                  <div className="border border-border rounded-lg p-5 bg-secondary/20 mb-6">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="font-medium text-foreground">{String(t("enrollment.choosePlan"))}</p>
                        <p className="text-sm text-muted-foreground">
                          {loadingLicensedUsers ? (
                            <span className="inline-flex items-center gap-1.5">
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              {String(t("enrollment.loadingLicensedUsers", "Loading licensed users…"))}
                            </span>
                          ) : (
                            String(t("enrollment.basedOnUsers", { count: userCount.toLocaleString() } as any))
                          )}
                        </p>
                      </div>
                      {/* Billing toggle */}
                      <div className="flex items-center gap-2 text-sm">
                        <span className={billingAnnual ? "text-muted-foreground" : "font-medium text-foreground"}>
                          {String(t("pricing.monthly"))}
                        </span>
                        <button
                          onClick={() => setBillingAnnual(!billingAnnual)}
                          className="relative w-10 h-5 rounded-full bg-border transition-colors"
                          type="button"
                        >
                          <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-primary transition-transform ${billingAnnual ? "translate-x-5" : ""}`} />
                        </button>
                        <span className={billingAnnual ? "font-medium text-foreground" : "text-muted-foreground"}>
                          {String(t("pricing.annual"))} <span className="text-accent font-medium">{String(t("pricing.discount"))}</span>
                        </span>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-3 gap-3">
                      {planOptions.map((plan) => {
                        const features = t(`pricing.${plan.key}.features`, { returnObjects: true }) as string[];
                        const price = plan.custom
                          ? t("enrollment.customQuote")
                          : billingAnnual ? plan.annualPrice : plan.monthlyPrice;

                        return (
                          <button
                            key={plan.id}
                            type="button"
                            onClick={() => {
                              setSelectedPlan(plan.id);
                              if (plan.custom) setSelectedExtensions([]);
                            }}
                            className={`relative text-left p-4 rounded-xl border transition-all ${
                              selectedPlan === plan.id
                                ? "border-primary bg-primary/5 ring-1 ring-primary/20"
                                : "border-border bg-card hover:border-primary/50"
                            }`}
                          >
                            {plan.recommended && (
                              <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-[10px] font-semibold px-2 py-0.5 rounded-full">
                                {String(t("pricing.recommended"))}
                              </span>
                            )}
                            <div className="flex items-center gap-2 mb-2">
                              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                                selectedPlan === plan.id ? "border-primary" : "border-muted-foreground/40"
                              }`}>
                                {selectedPlan === plan.id && <div className="w-2 h-2 rounded-full bg-primary" />}
                              </div>
                              <span className="font-display font-semibold text-foreground">{String(t(`pricing.${plan.key}.name`))}</span>
                            </div>
                            <p className="text-xs text-muted-foreground mb-2">{String(t(`pricing.${plan.key}.storage`))} · {String(t(`pricing.${plan.key}.users`))}</p>
                            <div className="flex items-baseline gap-1 mb-3">
                              <span className="text-xl font-bold text-foreground">{price}</span>
                              {!plan.custom && (
                                <span className="text-muted-foreground text-xs">{String(t("pricing.perMonth"))} {String(t("enrollment.exclVat"))}</span>
                              )}
                            </div>
                            {billingAnnual && !plan.custom && (
                              <p className="text-[10px] text-muted-foreground mb-2">{String(t("pricing.billedAnnually"))}</p>
                            )}
                            <ul className="space-y-1">
                              {features.map((f) => (
                                <li key={String(f)} className="flex items-start gap-1.5 text-xs text-muted-foreground">
                                  <Check className="h-3 w-3 text-accent mt-0.5 flex-shrink-0" />
                                  <span>{String(f)}</span>
                                </li>
                              ))}
                            </ul>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Storage Extensions - only if not custom */}
                  {!isCustomPlan && (
                    <div className="border border-border rounded-lg p-5 bg-secondary/20">
                      <p className="font-medium text-foreground mb-1">{String(t("pricing.storage.title"))}</p>
                      <p className="text-sm text-muted-foreground mb-4">{String(t("pricing.storage.subtitle"))}</p>
                      <div className="grid grid-cols-3 gap-3">
                        {storageExtensions.map((ext) => (
                          <button
                            key={ext.id}
                            type="button"
                            onClick={() => toggleExtension(ext.id)}
                            className={`text-left p-4 rounded-xl border transition-all ${
                              selectedExtensions.includes(ext.id)
                                ? "border-primary bg-primary/5 ring-1 ring-primary/20"
                                : "border-border bg-card hover:border-primary/50"
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                                selectedExtensions.includes(ext.id) ? "border-primary bg-primary" : "border-muted-foreground/40"
                              }`}>
                                {selectedExtensions.includes(ext.id) && <Check className="h-3 w-3 text-primary-foreground" />}
                              </div>
                              <span className="font-semibold text-foreground text-sm">{ext.volume}</span>
                            </div>
                            <div className="flex items-baseline gap-1 mt-2">
                              <span className="text-lg font-bold text-foreground">{getExtPrice(ext)}</span>
                              <span className="text-muted-foreground text-xs">{String(t("pricing.perMonth"))} {String(t("enrollment.exclVat"))}</span>
                            </div>
                            {billingAnnual && (
                              <p className="text-[10px] text-accent font-medium mt-0.5">-15% {String(t("enrollment.annualDiscount"))}</p>
                            )}
                            <p className="text-xs text-muted-foreground mt-0.5">{ext.perTb}/TB</p>
                          </button>
                        ))}
                      </div>
                      {/* <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1.5">
                        <Check className="h-3 w-3 text-accent" />
                        {String(t("pricing.storage.transparency"))}
                      </p> */}
                    </div>
                  )}

                  {/* Cost Summary */}
                  {!isCustomPlan && (() => {
                    const cost = getCostSummary();
                    if (!cost) return null;
                    return (
                      <div className="border border-primary/30 rounded-lg p-5 bg-primary/5 mt-6">
                        <p className="font-medium text-foreground mb-3">{String(t("enrollment.costSummary"))}</p>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">{String(t(`pricing.${selectedPlan}.name`))} {String(t("enrollment.planLabel"))}</span>
                            <span className="text-foreground font-medium">{formatPrice(cost.planMonthly)}{String(t("pricing.perMonth"))}</span>
                          </div>
                          {cost.extMonthly > 0 && (
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">{String(t("enrollment.storageExtensions"))}</span>
                              <span className="text-foreground font-medium">{formatPrice(cost.extMonthly)}{String(t("pricing.perMonth"))}</span>
                            </div>
                          )}
                          <div className="border-t border-border pt-2 mt-2 flex justify-between">
                            <span className="font-semibold text-foreground">{String(t("enrollment.totalMonthly"))}</span>
                            <span className="font-bold text-foreground text-lg">{formatPrice(cost.totalMonthly)}{String(t("pricing.perMonth"))} <span className="text-xs font-normal text-muted-foreground">{String(t("enrollment.exclVat"))}</span></span>
                          </div>
                          {billingAnnual && (
                            <div className="flex justify-between text-xs">
                              <span className="text-muted-foreground">{String(t("enrollment.totalAnnual"))}</span>
                              <span className="font-semibold text-foreground">{formatPrice(cost.totalAnnual)} {String(t("enrollment.exclVat"))}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  <div className="mt-8 flex justify-between">
                    <Button variant="ghost" onClick={prev} className="gap-2">
                      <ArrowLeft className="h-4 w-4" /> {String(t("enrollment.back"))}
                    </Button>
                    {isCustomPlan ? (
                      <Button
                        onClick={() => setSalesModalOpen(true)}
                        className="gap-2"
                      >
                        {String(t("enrollment.contactSales"))} <ArrowRight className="h-4 w-4" />
                      </Button>
                    ) : (
                      <Button onClick={next} className="gap-2">
                        {String(t("enrollment.continue"))} <ArrowRight className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </Card>
              </motion.div>
            )}

            {/* ───────── STEP 4: Payment ───────── */}
            {step === 3 && (
              <motion.div key="step4" variants={pageVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3 }}>
                <Card className="p-8 border-border bg-card">
                  <h2 className="font-display text-2xl font-bold text-foreground mb-1">{String(t("enrollment.step4Title"))}</h2>
                  <p className="text-muted-foreground text-sm mb-6">{String(t("enrollment.step4Subtitle"))}</p>

                  {/* Payment method toggle */}
                  <div className="flex gap-3 mb-6">
                    <button
                      type="button"
                      onClick={() => setPaymentMethod("card")}
                      className={`flex-1 flex items-center justify-center gap-2 p-4 rounded-lg border text-sm font-medium transition-all ${
                        paymentMethod === "card"
                          ? "border-primary bg-primary/5 text-foreground"
                          : "border-border bg-card text-muted-foreground hover:border-primary/50"
                      }`}
                    >
                      <CreditCard className="h-5 w-5" />
                      {String(t("enrollment.creditCard"))}
                    </button>
                    <button
                      type="button"
                      onClick={() => setPaymentMethod("sepa")}
                      className={`flex-1 flex items-center justify-center gap-2 p-4 rounded-lg border text-sm font-medium transition-all ${
                        paymentMethod === "sepa"
                          ? "border-primary bg-primary/5 text-foreground"
                          : "border-border bg-card text-muted-foreground hover:border-primary/50"
                      }`}
                    >
                      <Landmark className="h-5 w-5" />
                      {String(t("enrollment.sepaMandate"))}
                    </button>
                  </div>

                  {paymentMethod === "card" ? (
                    <div className="space-y-4">
                      <div>
                        <Label className="mb-1.5">{String(t("enrollment.cardNumber"))}</Label>
                        <Input placeholder="4242 4242 4242 4242" />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="mb-1.5">{String(t("enrollment.expiry"))}</Label>
                          <Input placeholder="MM / YY" />
                        </div>
                        <div>
                          <Label className="mb-1.5">CVC</Label>
                          <Input placeholder="123" />
                        </div>
                      </div>
                      <div>
                        <Label className="mb-1.5">{String(t("enrollment.cardHolder"))}</Label>
                        <Input placeholder={name || "John Doe"} />
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div>
                        <Label className="mb-1.5">IBAN</Label>
                        <Input placeholder="FR76 3000 6000 0112 3456 7890 189" />
                      </div>
                      <div>
                        <Label className="mb-1.5">BIC / SWIFT</Label>
                        <Input placeholder="BNPAFRPP" />
                      </div>
                      <div>
                        <Label className="mb-1.5">{String(t("enrollment.accountHolder"))}</Label>
                        <Input placeholder={company || "Acme Corp"} />
                      </div>
                      <p className="text-xs text-muted-foreground">{String(t("enrollment.sepaNotice"))}</p>
                    </div>
                  )}

                  <div className="mt-8 flex justify-between">
                    <Button variant="ghost" onClick={prev} className="gap-2">
                      <ArrowLeft className="h-4 w-4" /> {String(t("enrollment.back"))}
                    </Button>
                    <Button onClick={next} className="gap-2">
                      {String(t("enrollment.continue"))} <ArrowRight className="h-4 w-4" />
                    </Button>
                  </div>
                </Card>
              </motion.div>
            )}

            {/* ───────── STEP 5: Summary + Success ───────── */}
            {step === 4 && (
              <motion.div key="step5" variants={pageVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3 }}>
                <div className="space-y-6">
                  {/* Summary */}
                  <Card className="p-8 border-border bg-card">
                    <h2 className="font-display text-2xl font-bold text-foreground mb-6">
                      {company || "Your"} {String(t("enrollment.enrollmentSummary"))}
                    </h2>

                    <div className="space-y-3">
                      {[
                        { label: t("enrollment.m365Connected"), done: true },
                        { label: t("enrollment.encryptionReady"), done: true },
                        { label: `${getSelectedPlanStorage()} ${String(t("enrollment.storage"))}`, done: true },
                        { label: paymentMethod === "card" ? t("enrollment.cardPayment") : t("enrollment.sepaSigned"), done: true },
                      ].map((item, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm text-foreground">
                          <CheckCircle2 className="h-5 w-5 text-accent" />
                          <span>{item.label}</span>
                        </div>
                      ))}
                    </div>

                    <div className="mt-6 p-4 rounded-lg bg-secondary/30 border border-border">
                      <p className="text-sm text-foreground font-medium mb-2">{String(t("enrollment.firstSync"))}</p>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Clock className="h-4 w-4" />
                        <span>24–48h ({userCount.toLocaleString()} {String(t("enrollment.users"))})</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                        <BarChart3 className="h-4 w-4" />
                        <span>{String(t("enrollment.dailySyncs"))}: 02:00 CET</span>
                      </div>
                    </div>

                    <div className="mt-6">
                      <Button
                        onClick={handleIntegrateClick}
                        disabled={integrateLoading}
                        className="w-full gap-2"
                      >
                        {integrateLoading ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <ArrowRight className="h-4 w-4" />
                        )}{" "}
                        {String(t("enrollment.integrateTenant"))}
                      </Button>
                    </div>
                  </Card>

                  {/* Final success (inline toggle) */}
                </div>
              </motion.div>
            )}

            {/* ───────── SUCCESS SCREEN ───────── */}
            {(step as number) === 5 && (
              <motion.div key="success" variants={pageVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3 }}>
                <Card className="p-10 border-border bg-card text-center">
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-accent/10 mb-4">
                    <PartyPopper className="h-10 w-10 text-accent" />
                  </div>
                  <h2 className="font-display text-2xl font-bold text-foreground mb-2">
                    🎉 {company} {String(t("enrollment.tenantCreated"))}
                  </h2>

                  <Card className="max-w-xs mx-auto mt-6 p-5 border-border bg-secondary/20 text-left">
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{String(t("enrollment.statusLabel"))}</span>
                        <Badge className="bg-accent text-accent-foreground">{String(t("enrollment.waitingEnrollment"))}</Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{String(t("enrollment.users"))}</span>
                        <span className="font-medium text-foreground">{userCount.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{String(t("enrollment.storage"))}</span>
                        <span className="font-medium text-foreground">{getSelectedPlanStorage()}</span>
                      </div>
                    </div>
                  </Card>

                  <Button onClick={() => navigate("/dashboard")} className="mt-8 gap-2">
                    {String(t("enrollment.goToDashboard"))} <ArrowRight className="h-4 w-4" />
                  </Button>
                </Card>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>
    </div>
    </>
  );
}
